import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Share, Plus, Monitor, Smartphone } from 'lucide-react';

interface InstallInstructionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: {
    isIOS: boolean;
    isSafari: boolean;
    isChrome: boolean;
    isAndroid: boolean;
  };
}

export const InstallInstructionsModal: React.FC<InstallInstructionsModalProps> = ({
  open,
  onOpenChange,
  platform
}) => {
  const renderIOSInstructions = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 bg-white/5 border border-white/10 rounded-xl">
        <div className="w-10 h-10 bg-luxury-pinkBright/20 rounded-full flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-5 h-5 text-luxury-pinkBright" />
        </div>
        <div className="space-y-3 text-sm">
          <p className="font-semibold text-white">Safari no iPhone/iPad</p>
          <ol className="space-y-2 text-white/60 list-decimal list-inside">
            <li>Toque no ícone <Share className="inline w-4 h-4 mx-1 text-white/80" /> <strong className="text-white">Compartilhar</strong> na barra inferior</li>
            <li>Role para baixo e toque em <Plus className="inline w-4 h-4 mx-1 text-white/80" /> <strong className="text-white">"Adicionar à Tela de Início"</strong></li>
            <li>Toque em <strong className="text-white">"Adicionar"</strong> no canto superior direito</li>
            <li>O app aparecerá na sua tela de início! 🎉</li>
          </ol>
        </div>
      </div>
      
      <p className="text-xs text-white/40 text-center">
        O ícone do app ficará disponível na tela de início do seu dispositivo
      </p>
    </div>
  );

  const renderAndroidInstructions = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 bg-white/5 border border-white/10 rounded-xl">
        <div className="w-10 h-10 bg-luxury-pinkBright/20 rounded-full flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-5 h-5 text-luxury-pinkBright" />
        </div>
        <div className="space-y-3 text-sm">
          <p className="font-semibold text-white">Chrome/Edge no Android</p>
          <ol className="space-y-2 text-white/60 list-decimal list-inside">
            <li>O navegador mostrará automaticamente um <strong className="text-white">banner de instalação</strong></li>
            <li>Toque em <strong className="text-white">"Instalar"</strong> ou <strong className="text-white">"Adicionar"</strong></li>
            <li>Ou toque no menu <strong className="text-white">⋮</strong> (três pontos) no canto superior</li>
            <li>Selecione <strong className="text-white">"Instalar app"</strong> ou <strong className="text-white">"Adicionar à tela inicial"</strong></li>
          </ol>
        </div>
      </div>
      
      <p className="text-xs text-white/40 text-center">
        O app ficará disponível na gaveta de apps e tela de início
      </p>
    </div>
  );

  const renderDesktopInstructions = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 bg-white/5 border border-white/10 rounded-xl">
        <div className="w-10 h-10 bg-luxury-pinkBright/20 rounded-full flex items-center justify-center flex-shrink-0">
          <Monitor className="w-5 h-5 text-luxury-pinkBright" />
        </div>
        <div className="space-y-3 text-sm">
          <p className="font-semibold text-white">Chrome/Edge no Desktop</p>
          <ol className="space-y-2 text-white/60 list-decimal list-inside">
            <li>Clique no ícone <Plus className="inline w-4 h-4 mx-1 text-white/80" /> <strong className="text-white">"Instalar"</strong> na barra de endereço</li>
            <li>Ou abra o menu <strong className="text-white">⋮</strong> (três pontos) no canto superior</li>
            <li>Selecione <strong className="text-white">"Instalar Interlaced..."</strong></li>
            <li>Confirme a instalação na janela que aparecer</li>
          </ol>
        </div>
      </div>
      
      <p className="text-xs text-white/40 text-center">
        O app aparecerá como um aplicativo independente no seu sistema
      </p>
    </div>
  );

  const getInstructions = () => {
    if (platform.isIOS || platform.isSafari) {
      return renderIOSInstructions();
    }
    if (platform.isAndroid) {
      return renderAndroidInstructions();
    }
    return renderDesktopInstructions();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-gradient-to-br from-black via-black to-gray-900 border-white/10" hasTitle>
        <DialogHeader>
          <DialogTitle className="text-xl text-white">📱 Como Instalar o App</DialogTitle>
          <DialogDescription className="text-white/60">
            Siga as instruções abaixo para instalar o Interlaced no seu dispositivo
          </DialogDescription>
        </DialogHeader>

        {getInstructions()}

        <div className="flex justify-end pt-2">
          <Button
            onClick={() => onOpenChange(false)}
            className="bg-gradient-to-b from-[#C4406B] to-[#9F3764] hover:opacity-90 text-white border-0"
          >
            Entendi
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
